import { registerFigure } from "./base.js";
/**
 * Two or three things set side by side, on the same rows.
 *
 * "What's the difference between X and Y" is one of the most common shapes a
 * child's question takes, and prose is genuinely bad at it: a paragraph makes
 * the reader hold four facts about a reptile in their head while they read four
 * about an amphibian. A grid does the holding for them, and the comparison is
 * what they came for.
 *
 * Laid out row-major in `labels` — the column headings first, then each row —
 * rather than as its own field, because the shared wire shape is one flat
 * object and this fits inside it without widening anything for everyone else.
 * `cols` says how many columns to cut it into.
 */
export const contrastFigure = registerFigure({
    kind: "contrast",
    summary: "A small grid setting two or three things against each other.",
    chooseWhen: "The question is a difference or a comparison — reptiles against " +
        "amphibians, solid against liquid against gas, herbivore against " +
        "carnivore. Every row must say something about EVERY column, one cell per " +
        "column and nothing else in the row: a row that only applies to one of " +
        "them, or that names the category being compared, belongs in the answer " +
        "text and not the grid.",
    issues(figure) {
        const labels = figure.labels ?? [];
        const cols = figure.cols ?? 0;
        if (cols < 2 || cols > 3)
            return ["A contrast needs 2 or 3 columns."];
        if (labels.length < cols * 2) {
            return ["A contrast needs the headings plus at least one row under them."];
        }
        if (labels.length % cols !== 0) {
            return [`${labels.length} labels do not divide into ${cols} columns — every row must be full.`];
        }
        if (labels.some((l) => l.trim().length === 0))
            return ["A cell cannot be blank."];
        return [];
    },
    normalise(figure) {
        const cols = figure.cols ?? 2;
        const labels = (figure.labels ?? []).map((l) => l.trim());
        return {
            kind: "contrast",
            caption: figure.caption,
            cols,
            // Drop a trailing partial row rather than rendering a ragged grid. It
            // can't happen past validation, but normalise runs on its own too.
            labels: labels.slice(0, Math.floor(labels.length / cols) * cols),
        };
    },
    example: {
        kind: "contrast",
        caption: "Skin is the quickest way to tell them apart.",
        cols: 2,
        labels: [
            "Reptile",
            "Amphibian",
            "Dry scales",
            "Moist skin",
            "Eggs on land",
            "Eggs in water",
        ],
    },
});
//# sourceMappingURL=contrast.js.map